import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'

export default class AppointmentProductsController extends Controller {
  static targets = ['searchInput', 'dropdown', 'productRows', 'totalAmount', 'emptyMessage']
  static values = { searchUrl: String }

  initialize () {
    this.products = new Map()
    this.searchTimeout = null
    this.searchAbort = null
    this.nextIdx = 0
  }

  connect () {
    // Load existing products from pre-rendered hidden rows
    let maxIdx = -1
    this.productRowsTarget.querySelectorAll('[data-product-id]').forEach(row => {
      this.products.set(row.dataset.productId, true)
      // Track highest existing index from input names like products_attributes[N]
      const input = row.querySelector('input[name*="products_attributes"]')
      if (input) {
        const match = input.name.match(/\[(\d+)\]/)
        if (match) maxIdx = Math.max(maxIdx, parseInt(match[1], 10))
      }
    })
    this.nextIdx = maxIdx + 1
    this.#updateTotal()
    this.#toggleEmpty()

    this._repeatHandler = (e) => this.onRepeatProducts(e)
    document.addEventListener('repeat-products', this._repeatHandler)
  }

  disconnect () {
    document.removeEventListener('repeat-products', this._repeatHandler)
    if (this.searchAbort) this.searchAbort.abort()
    clearTimeout(this.searchTimeout)
  }

  onSearch () {
    clearTimeout(this.searchTimeout)
    const query = this.searchInputTarget.value.trim()

    if (query.length < 2) {
      this.#hideDropdown()
      return
    }

    this.searchTimeout = setTimeout(() => {
      if (this.searchAbort) this.searchAbort.abort()
      this.searchAbort = new AbortController()

      fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.searchAbort.signal
      })
        .then(r => r.json())
        .then(data => this.#renderDropdown(data))
        .catch(e => { if (e.name !== 'AbortError') this.#hideDropdown() })
    }, 300)
  }

  selectProduct (event) {
    const item = event.currentTarget
    const product = JSON.parse(item.dataset.product)

    if (this.products.has(String(product.id))) return

    this.#addProductRow(product)
    this.searchInputTarget.value = ''
    this.#hideDropdown()
  }

  removeProduct (event) {
    const row = event.currentTarget.closest('[data-product-id]')
    this.products.delete(row.dataset.productId)
    row.remove()
    this.#updateTotal()
    this.#toggleEmpty()
  }

  onRepeatProducts (event) {
    const products = event.detail.products
    if (!products) return

    products.forEach(p => {
      if (!this.products.has(String(p.id))) {
        this.#addProductRow(p, p.quantity || null)
      }
    })
  }

  onQuantityChange (event) {
    const row = event.currentTarget.closest('[data-product-id]')
    const qty = parseFloat(event.currentTarget.value) || 0
    const price = parseFloat(row.dataset.price) || 0
    const rowTotal = row.querySelector('[data-row-total]')
    if (rowTotal) {
      rowTotal.textContent = this.#formatCurrency(qty * price)
    }
    this.#updateTotal()
  }

  // Private

  #renderDropdown (data) {
    if (!data.length) {
      this.#hideDropdown()
      return
    }

    const html = data.map(p => {
      const added = this.products.has(String(p.id))
      const isBulk = p.is_bulk === true || p.unit === 'л'
      const priceLabel = isBulk ? `${this.#escapeHtml(this.#formatCurrency(p.price))}<span class="text-xs text-muted-foreground ml-0.5">/л</span>` : this.#escapeHtml(this.#formatCurrency(p.price))
      const stockNum = this.#escapeHtml(String(p.stock))
      const stockLabel = isBulk ? `${stockNum} л` : `${stockNum} ${this.#escapeHtml(p.unit)}`
      return `
        <button type="button"
          class="w-full text-left px-4 py-2.5 hover:bg-muted/50 border-b border-border/30 last:border-b-0 flex items-center justify-between gap-4 ${added ? 'opacity-40 pointer-events-none' : 'cursor-pointer'}"
          data-action="click->appointment-products#selectProduct"
          data-product='${JSON.stringify(p).replace(/'/g, '&#39;')}'>
          <div class="flex flex-col min-w-0 gap-0.5">
            <span class="text-sm font-medium truncate">${this.#escapeHtml(p.name)}</span>
            <span class="text-xs text-muted-foreground">${this.#escapeHtml(p.article)}${p.brand ? ' · ' + this.#escapeHtml(p.brand) : ''}${isBulk ? ' · <span class="text-blue-500">разлив</span>' : ''}</span>
          </div>
          <div class="flex items-center gap-4 shrink-0 text-sm">
            <span class="font-semibold tabular-nums">${priceLabel}</span>
            <span class="${p.stock > 0 ? 'text-green-600' : 'text-red-500'} tabular-nums text-xs">${stockLabel}</span>
          </div>
        </button>
      `
    }).join('')

    this.dropdownTarget.innerHTML = html
    this.dropdownTarget.classList.remove('hidden')
  }

  #hideDropdown () {
    this.dropdownTarget.classList.add('hidden')
    this.dropdownTarget.innerHTML = ''
  }

  #addProductRow (product, quantity = null) {
    const id = String(product.id)
    this.products.set(id, true)

    const isBulk = product.is_bulk === true || product.unit === 'л'
    const defaultQty = isBulk ? 1.0 : 1
    const qty = quantity !== null ? quantity : defaultQty
    const step = isBulk ? '0.1' : '1'
    const min = isBulk ? '0.1' : '1'
    const qtyLabel = isBulk ? 'л' : product.unit || 'шт'
    const priceNote = isBulk ? `<span class="text-xs text-muted-foreground">за л</span>` : ''

    const idx = this.nextIdx++
    const row = document.createElement('tr')
    row.dataset.productId = id
    row.dataset.price = product.price
    row.dataset.isBulk = isBulk ? '1' : '0'
    row.className = 'border-b border-border/40 last:border-b-0'

    row.innerHTML = `
      <td class="py-2 pr-3">
        <div class="flex flex-col">
          <span class="text-sm font-medium truncate max-w-[200px]" title="${this.#escapeHtml(product.name)}">${this.#escapeHtml(product.name)}</span>
          <span class="text-xs text-muted-foreground">${this.#escapeHtml(product.article)}</span>
        </div>
        <input type="hidden" name="appointment[products_attributes][${idx}][product_id]" value="${product.id}">
        <input type="hidden" name="appointment[products_attributes][${idx}][price_at_time]" value="${product.price}">
      </td>
      <td class="py-2 px-2 text-right tabular-nums text-sm">
        <div class="flex flex-col items-end gap-0.5">
          <span>${this.#formatCurrency(product.price)}</span>
          ${priceNote}
        </div>
      </td>
      <td class="py-2 px-2 w-24">
        <div class="flex items-center gap-1">
          <input type="number" name="appointment[products_attributes][${idx}][quantity]"
                 value="${qty}" min="${min}" step="${step}"
                 class="form__field text-center text-sm tabular-nums w-full"
                 data-action="input->appointment-products#onQuantityChange">
          <span class="text-xs text-muted-foreground shrink-0">${this.#escapeHtml(qtyLabel)}</span>
        </div>
      </td>
      <td class="py-2 px-2 text-right tabular-nums text-sm font-medium" data-row-total>${this.#formatCurrency(product.price * qty)}</td>
      <td class="py-2 pl-2">
        <button type="button" class="text-muted-foreground hover:text-red-500 transition-colors"
                data-action="click->appointment-products#removeProduct">
          <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>
      </td>
    `

    this.productRowsTarget.querySelector('tbody').appendChild(row)
    this.#updateTotal()
    this.#toggleEmpty()
  }

  #updateTotal () {
    let total = 0
    this.productRowsTarget.querySelectorAll('[data-product-id]').forEach(row => {
      const price = parseFloat(row.dataset.price) || 0
      const qty = parseFloat(row.querySelector('input[type="number"]')?.value) || 0
      total += price * qty
    })
    this.totalAmountTarget.textContent = this.#formatCurrency(total)
    this.totalAmountTarget.closest('[data-total-row]').classList.toggle('hidden', total === 0)
  }

  #toggleEmpty () {
    const hasProducts = this.products.size > 0
    this.emptyMessageTarget.classList.toggle('hidden', hasProducts)
    this.productRowsTarget.classList.toggle('hidden', !hasProducts)
  }

  #formatCurrency (amount) {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2 }).format(amount)
  }

  #escapeHtml (text) {
    const div = document.createElement('div')
    div.textContent = text || ''
    return div.innerHTML
  }
}

stimulus.register('appointment-products', AppointmentProductsController)
